import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ADMIN, Animations, CORPORATE_ADMIN, ResponsiveRanges, SUPER_ADMIN } from '../../shared/constants';
import * as _ from 'lodash';
import { AccountInfoComponent } from './components/account-info/account-info.component';
import { AuthService } from '@app/shared/services/auth.service';
import { AgentModel, sidenavItems } from '@app/shared/models/auth.model';
import { LocationService } from '@app/shared/services/location.service';
import { PPUAlertService } from '@app/shared/services/ppu-alert.service';
import PPURouteBuilder from '@app/shared/utilities/route.builder';
import { PPUCarrierModel, PPULocationModel } from '@app/shared/models/location.model';
import { mergeMap } from 'rxjs';
import { PageTitleService } from '@app/shared/services/page-title.service';
import { LocationPermissionModel } from '@app/shared/models/location.model';
import { MatSidenav } from '@angular/material/sidenav';
import { TranslateService } from '@ngx-translate/core';
import { LocationStateService } from '@app/shared/services/location-state.service';

@Component({
    selector: 'app-authenticated',
    animations: [Animations.OpenClose, Animations.RotateIcon],
    templateUrl: './authenticated.component.html',
    standalone: false
})
export class AuthenticatedComponent implements OnInit {
  @ViewChild('sidenav') sidenav!: MatSidenav;
  
  public isLoading: boolean = true;
  public hasLocationAccess: boolean = true;
  public agentInfo: AgentModel = {} as AgentModel;
  public navItems: Array<sidenavItems> = new Array<sidenavItems>();
  public locationName: string = '';
  public adminRole: boolean = false;
  public locations: Array<PPULocationModel> = new Array<PPULocationModel>();
  public currentLocation!: number;
  public dropOffCarriers: PPUCarrierModel[] = [];
  public halCarriers: PPUCarrierModel[] = [];
  public screenWidth: number = 0;
  public isMobile: boolean = false; // Tracks mobile screen size

  // Sidenav state properties
  public sidenavMode: 'side' | 'over' = 'side';
  public sidenavOpened: boolean = true;
  public sidenavExpanded: boolean = true;



  constructor(
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService,
    private locationService: LocationService,
    private ppuAlertService: PPUAlertService,
    private pageTitleService: PageTitleService,
    public translate: TranslateService,
    private locationState: LocationStateService
  ) {}

  async ngOnInit() {
    this.handleScreenSizeChange();
    
    this.agentInfo = await this.authService.getAgentInfo();

    // Corporate Admin tokens contain the parent ID, not an operational child location ID.
    if (this.agentInfo.role === CORPORATE_ADMIN) {
      this.adminRole = true;
      this.initializeCorporateAdmin();
      return;
    }

    this.authService
      .isAdminRole()
      .pipe(
        mergeMap((result) => {
          this.adminRole = result;
          if (this.adminRole) {
            this.locations = this.locationService.locations ?? [];
          }
          // Resolve admin permissions for the selected child location, not the corporate parent.
          return this.locationService.GetUserLocationPermission();
        })
      )
        .subscribe({
          next: (result) => {
            // Admins: restore saved location if valid, otherwise use first location
            if (this.adminRole && this.locations && this.locations.length > 0) {
              const savedId = this.locationState.ppuLocationId();
              this.currentLocation = savedId && this.locations.some(l => l.id === savedId)
                ? savedId
                : this.locations[0].id;
            } else {
              // Non-admins: use permission location
              this.currentLocation = result.ppuLocationId;
            }
            
            // Update signal
            this.locationState.ppuLocationId.set(this.currentLocation);
            
            this.locationName = `${result.locationCode} - ${result.locationName}`;
          
          // Create and filter nav items
          this.navItems = this.createFilteredNavItems(result);
          
          this.dropOffCarriers = result.dropOffCarriers;
          this.halCarriers = result.halCarriers;
          this.updateNavLinks(this.router.url);
          this.isLoading = false;
        },
        error: (error) => {
          this.ppuAlertService.errorEvent(error[0]);
          this.hasLocationAccess = false;
          this.isLoading = false;
        },
      });
  }

  public changeLocationId(id: number) {
    if (id > 0) {
      this.locationState.ppuLocationId.set(id);

      if (this.agentInfo.role === CORPORATE_ADMIN) {
        this.currentLocation = id;
        const route = this.navItems.find(x => this.router.url.includes(x.routerLink))?.routerLink
          ?? this.navItems[0]?.routerLink;
        if (route) {
          this.router.navigate([route]);
        }
        return;
      }

      this.isLoading = true;
      this.locationService.GetLocationPermission(id, true).subscribe({
        next: (result) => {
          this.locationName = `${result.locationCode} - ${result.locationName}`;
          
          // Create and filter nav items
          this.navItems = this.createFilteredNavItems(result);
          
          this.dropOffCarriers = result.dropOffCarriers;
          this.halCarriers = result.halCarriers;
          const route = this.navItems.find(x => this.router.url.includes(x.routerLink))?.routerLink ?? this.navItems[0].routerLink;
          this.router.navigate([route]);
          this.updateNavLinks(route);
          this.currentLocation = id;
          this.isLoading = false;
        },
        error: (error) => {
          this.ppuAlertService.errorEvent(error[0]);
          this.isLoading = false;
        },
      });
    }
  }

  private initializeCorporateAdmin(): void {
    this.locationService.GetCorporateChildLocations().subscribe({
      next: (locations) => {
        this.locations = locations;
        // The backend returns only active children for operational access.
        if (!locations.length) {
          this.hasLocationAccess = false;
          this.isLoading = false;
          return;
        }

        const savedId = this.locationState.ppuLocationId();
        this.currentLocation = savedId && locations.some(location => location.id === savedId)
          ? savedId
          : locations[0].id;
        this.locationState.ppuLocationId.set(this.currentLocation);
        this.navItems = this.createFilteredNavItems(null);
        this.updateNavLinks(this.router.url);
        this.isLoading = false;
      },
      error: (error) => {
        this.ppuAlertService.errorEvent(error[0]);
        this.hasLocationAccess = false;
        this.isLoading = false;
      },
    });
  }

  // Creates navigation items from permissions and adds admin tool for authorized users
  private createFilteredNavItems(permissions: LocationPermissionModel | null): sidenavItems[] {
    let navItems: sidenavItems[] = [];
    if (this.agentInfo.role !== CORPORATE_ADMIN && permissions) {
      navItems = PPURouteBuilder.createNavItems(permissions);
    }

    // Add reporting for all locations
    navItems.push({
      routerLink: 'reporting',
      iconType: 'icons',
      iconClass: 'graph',
      title: 'Reporting',
      active: false,
    });
    
    // Add admin tool for authorized users
    if (this.agentInfo.role === SUPER_ADMIN || this.agentInfo.role === ADMIN || this.agentInfo.role === CORPORATE_ADMIN) {
      navItems.push({
        routerLink: 'admintool',
        iconType: 'icons',
        iconClass: 'map',
        title: 'Admin tool',
        active: false,
      });
    }
    
    return navItems;
  }

  public gotoNav(navItem: sidenavItems) {
    this.navItems.forEach((item) => {
      item.active = item === navItem;
    });
    this.router.navigate([navItem.routerLink]);
    
    // Desktop collapse
    if (this.sidenavExpanded && this.screenWidth < ResponsiveRanges.largeDesktop && !this.isMobile) {
      this.sidenavExpanded = false;
    }

    // Only close on mobile, desktop stays as is
    if (this.isMobile) {
      this.closeSidenav();
    }

  }

  private updateNavLinks(routerUrl: string) {
    this.navItems.forEach((item) => {
      item.active = false;
      if ((routerUrl || '').includes(item.routerLink)) {
        item.active = true;
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.handleScreenSizeChange();
  }

  private initializeSidenav() {
    // Initialize sidenav state based on screen size
    if (this.isMobile) {
      this.sidenavMode = 'over';
      this.sidenavOpened = false;
      this.sidenavExpanded = true; // Always expanded on mobile when opened
    } else {
      this.sidenavMode = 'side';
      this.sidenavOpened = true;
      this.sidenavExpanded = this.screenWidth >= ResponsiveRanges.largeDesktop;
    }
  }

  public toggle() {
    // Mobile: toggle open/closed (hamburger button)
    if (this.isMobile) {
      this.sidenav.toggle();
    } else {
      // Desktop: toggle expansion (chevron button)
      this.sidenavExpanded = !this.sidenavExpanded;
    }
  }

  public closeSidenav() {
    this.sidenav.close();
  }

  public collapseIfExpanded() {
    if (this.sidenavExpanded && this.screenWidth < ResponsiveRanges.largeDesktop && !this.isMobile) {
      setTimeout(() => {
        this.sidenavExpanded = false;
      }, 10);
    }
  }

  public getAccountView() {
    this.dialog.open(AccountInfoComponent, {
      data: this.agentInfo,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      hasBackdrop: true,
      position: {
        top: `65px`,
        right: `50px`,
      },
      panelClass: 'account-info',
    });
  }

  public async logout() {
    await this.authService.logout();
  }

  public onActivate(component: any) {
    if (component.refresh) {
      component.refresh(this.currentLocation);
    }
    if(component.getDropOffCarriers){
      component.getDropOffCarriers(this.dropOffCarriers);
    }
    if(component.getHALCarriers){
      component.getHALCarriers(this.halCarriers);
    }
    this.pageTitleService.setPageTitle();
  }

  private handleScreenSizeChange() {
    this.screenWidth = window.innerWidth;
    this.isMobile = this.screenWidth < ResponsiveRanges.mobile;
    this.initializeSidenav();
  }
}
